import React from 'react';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../lib/reportRoleColors';

/**
 * Recharts custom tooltip with Sleek styling — white card with shadow,
 * rounded corners, colored dots + mono values. Wire as `content`:
 *
 *   <Tooltip content={<SleekChartTooltip />} />
 *
 * Renders `null` when inactive so recharts can suppress the hover layer.
 *
 * Typing note: recharts `TooltipProps` omits `active` / `payload` / `label`
 * (they're read from context and injected by the parent Tooltip into the
 * `content` element). The shape recharts injects matches `TooltipContentProps`,
 * but that type isn't exported as a public surface, and `TooltipProps`
 * itself lacks the runtime fields. We type the local shape inline so we
 * don't lean on a non-public export.
 */
interface SleekTooltipPayload {
  name?: React.ReactNode;
  value?: number | string;
  color?: string;
}

interface SleekTooltipProps {
  active?: boolean;
  payload?: SleekTooltipPayload[];
  label?: React.ReactNode;
}

export function SleekChartTooltip({ active, payload, label }: SleekTooltipProps) {
  // STBW-2 — read via the hook so a <ThemeOverrideProvider> (Studio
  // preview / print wrap) flows through; falls back to store + DEFAULT_THEME
  // when no Provider wraps, so ad-hoc mounts (and recharts' detached render)
  // still resolve to a valid theme. This was the only sleek primitive with
  // zero theme wiring.
  const theme = useEffectiveReportTheme();
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      className="border rounded-xl p-3 text-sm"
      style={{
        backgroundColor: roleColor(theme, 'subtleBg'),
        borderColor: roleColor(theme, 'hair'),
      }}
    >
      {label != null && label !== '' && (
        <div
          className="font-semibold mb-2 pb-2 border-b"
          style={{ color: roleColor(theme, 'ink'), borderColor: roleColor(theme, 'rule') }}
        >
          {label}
        </div>
      )}
      <div className="space-y-1.5">
        {payload.map((row, i) => (
          <div key={i} className="flex justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              {/* Per-series dot keeps the recharts series color verbatim. */}
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: row.color }}
                aria-hidden="true"
              />
              <span style={{ color: roleColor(theme, 'muted') }}>{row.name}</span>
            </div>
            <span className="font-mono font-medium" style={{ color: roleColor(theme, 'ink') }}>
              {String(row.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
